// @flow strict-local

import type {Brand} from 'src/types/brands';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {Button} from '@spaced-out/ui-lib/lib/button';
import {formatDateTime} from './utils';

import makeClassNameComponent, {
  type ClassNameComponent,
} from 'src/utils/makeClassNameComponent';

import {HappyCalendar} from 'src/components/scheduler/auth-landing-page.jsx';
import NotFoundCalendar from 'src/images/scheduler/not-found.svg';
import FrowningCalendar from 'src/images/scheduler/frowning-calendar.svg';
import {BrandingHeader} from './branding.jsx';

import css from '../booking.css';


export function Container({
  children,
  brand,
  language,
  persistLanguage = true,
}: {
  children: React.Node,
  brand: ?Brand,
  language?: string,
  persistLanguage?: boolean,
}): React.Node {
  return (
    <div
      className={css.bookingContainer}
      style={{backgroundColor: brand?.color || 'white'}}
    >
      <BrandingHeader
        brand={brand}
        language={language}
        persistLanguage={persistLanguage}
      />
      {children}
    </div>
  );
}

export const BookingHeader: ClassNameComponent<'h1'> = makeClassNameComponent(
  css.bookingHeader,
  'h1',
);

export const BookingBox: ClassNameComponent<> = makeClassNameComponent(
  css.bookingBox,
);

export const BookingBody: ClassNameComponent<> = makeClassNameComponent(
  css.bookingBody,
);

export function DonePage({children}: {children: React.Node}): React.Node {
  return (
    <BookingBox className={css.donePage}>
      <div className={css.successWrapper}>{children}</div>
    </BookingBox>
  );
}

export function SuccessPage({date}: {date: Date}): React.Node {
  const {t, makeKey} = useLabelTranslation();

  const formattedDate = formatDateTime(date, t, makeKey);

  return (
    <DonePage>
      <HappyCalendar />
      <DoneText>{`${t(
        'Your_meeting_has_been_scheduled',
        'Your meeting has been scheduled',
      )}.`}</DoneText>
      <DoneSubtext>
        <div>{formattedDate}</div>
      </DoneSubtext>
    </DonePage>
  );
}

export function SuccessfulRescheduleRequestPage(): React.Node {
  const {t} = useLabelTranslation();

  return (
    <DonePage>
      <HappyCalendar />
      <DoneText>{`${t('Success', 'Success')}!`}</DoneText>
      <DoneSubtext>
        <div>{`${t(
          'You\'ve_successfully_requested_that_this_meeting_be_rescheduled',
          'You\'ve successfully requested that this meeting be rescheduled',
        )}.`}</div>
      </DoneSubtext>
    </DonePage>
  );
}

export function NoAvailabilitySuccess(): React.Node {
  const {t} = useLabelTranslation();

  return (
    <DonePage>
      <HappyCalendar />
      <DoneText>{`${t('Thank_you', 'Thank you')}.`}</DoneText>
      <DoneSubtext>{`${t(
        'Your_request_has_been_sent',
        'Your request has been sent',
      )}.`}</DoneSubtext>
    </DonePage>
  );
}

export function NotFoundPage(): React.Node {
  const {t} = useLabelTranslation();

  return (
    <DonePage>
      <NotFoundCalendar />
      <DoneText>{`${t(
        'Uh_oh_This_meeting_does_not_exist',
        'Uh oh! This meeting does not exist',
      )}.`}</DoneText>
      <DoneSubtext>{`${t(
        'It_might_have_been_moved_or_deleted',
        'It might have been moved or deleted',
      )}.`}</DoneSubtext>
    </DonePage>
  );
}

export function MaintenancePage(): React.Node {
  const {t} = useLabelTranslation();

  return (
    <DonePage>
      <NotFoundCalendar />
      <DoneText>{`${t(
        'The_scheduler_is_undergoing_maintenance',
        'The scheduler is undergoing maintenance',
      )}.`}</DoneText>
      <DoneSubtext>
        {t('You_may_visit_our', 'You may visit our')}{' '}
        <a
          href="https://status.sensehq.com"
          target="_blank"
          style={{textDecoration: 'underline'}}
        >
          {t('status_page', 'status page')}
        </a>{' '}
        {`${t('for_more_information', 'for more information')}.`}
      </DoneSubtext>
    </DonePage>
  );
}

export function CantCancelPage({
  cancellationPolicy,
}: {
  cancellationPolicy: ?string,
}): React.Node {
  const {t} = useLabelTranslation();

  return (
    <DonePage>
      <FrowningCalendar />
      <DoneText>{`${t(
        'It\'s_too_late_to_cancel',
        'It\'s too late to cancel',
      )}.`}</DoneText>
      <DoneText>
        {cancellationPolicy
          ? cancellationPolicy
          : `${t(
              'Please_contact_your_host_to_reschedule_or_cancel',
              'Please contact your host to reschedule or cancel',
            )}.`}
      </DoneText>
    </DonePage>
  );
}

export function NoAvailabilityPage({
  onConfirm,
}: {
  onConfirm: (event: SyntheticEvent<HTMLElement>) => mixed,
}): React.Node {
  const {t} = useLabelTranslation();

  return (
    <DonePage>
      <FrowningCalendar />
      <DoneText>{`${t(
        'Sorry_We_couldn\'t_find_an_available_time_to_schedule_this_meeting',
        'Sorry! We couldn\'t find an available time to schedule this meeting',
      )}.`}</DoneText>
      <DoneText>
        <Button onClick={onConfirm}>
          {t('Notify_the_host', 'Notify the host')}
        </Button>
      </DoneText>
    </DonePage>
  );
}

export function BookingLinkIsAlreadyUsed(): React.Node {
  const {t} = useLabelTranslation();

  return (
    <DonePage>
      <NotFoundCalendar />
      <DoneText>{`${t(
        'Uh_oh_This_link_is_expired',
        'Uh oh! This link is expired',
      )}.`}</DoneText>
      <DoneSubtext>{`${t(
        'Please_retry_with_a_new_link',
        'Please retry with a new link',
      )}.`}</DoneSubtext>
    </DonePage>
  );
}

export function APIFailed(): React.Node {
  const {t} = useLabelTranslation();

  return (
    <DonePage>
      <NotFoundCalendar />
      <DoneText>{`${t(
        'Something_went_wrong',
        'Something went wrong',
      )}!`}</DoneText>
      <DoneSubtext>{`${t(
        'Please_try_again_later',
        'Please try again later',
      )}.`}</DoneSubtext>
    </DonePage>
  );
}

export function TemplateNotFoundPage(): React.Node {
  const {t} = useLabelTranslation();
  return (
    <DonePage>
      <NotFoundCalendar />
      <DoneText>
        {`${t('Something_went_wrong', 'Something went wrong')}!`}
      </DoneText>
    </DonePage>
  );
}

const DoneText = makeClassNameComponent(css.successText, 'p');
const DoneSubtext = makeClassNameComponent(css.successTime, 'p');

//$FlowFixMe
export const getErrorMessagesMap = (t) => {
  return {
    unavailableTimeSlot: `'${t(
      'The_selected_time_slot_is_unavailable_Please_choose_a_different_slot',
      'The selected time slot is unavailable. Please choose a different slot',
    )}.`,
    sameTemplateEventsFound: `${t(
      'A_similar_event_already_exists_Please_pick_a_different_date',
      'A similar event already exists. Please pick a different date',
    )}.`,
    phoneNumberInvalid: `${t(
      'Please_enter_a_valid_phone_number',
      'Please enter a valid phone number',
    )}.`,
    emailInvalid: `${t(
      'Please_enter_a_valid_email_address',
      'Please enter a valid email address',
    )}.`,
    invalidQuestionAnswer: `${t(
      'One_or_more_answers_provided_are_invalid_Please_review_your_responses_and_try_again',
      'One or more answers provided are invalid. Please review your responses and try again',
    )}.`,
    timeSlotNotAvailable: `${t(
      'The_time_slot_you_selected_is_no_longer_available_Please_choose_a_different_slot',
      'The time slot you selected is no longer available. Please choose a different slot',
    )}.`,
    timeSlotAlreadyBooked: `${t(
      'The_selected_time_slot_has_already_been_booked_Please_choose_a_different_slot',
      'The selected time slot has already been booked. Please choose a different slot',
    )}.`,
    unknownError: `${t(
      'An_unknown_error_occurred_Please_try_again_later',
      'An unknown error occurred. Please try again later',
    )}.`,
  };
};

export const errorMap = {
  UnavailableTimeSlot: 'unavailableTimeSlot',
  SameTemplateEventsFound: 'sameTemplateEventsFound',
  PhoneNumberInvalid: 'phoneNumberInvalid',
  EmailInvalid: 'emailInvalid',
  InvalidQuestionAnswer: 'invalidQuestionAnswer',
  TimeSlotNotAvailable: 'timeSlotNotAvailable',
  TimeSlotAlreadyBooked: 'timeSlotAlreadyBooked',
};

// $FlowFixMe
export const getEventConfirmationErrorMessagesMap = (t) => {
  const errorMap = {
    InvalidNumber: t(
      'Answer_must_be_a_valid_number',
      'Answer must be a valid number',
    ),
    InvalidPhoneNumber: t(
      'Answer_must_be_a_valid_phone_number',
      'Answer must be a valid phone number',
    ),
    InvalidEmailAddress: t(
      'Answer_must_be_a_valid_email_address',
      'Answer must be a valid email address',
    ),
    InvalidChoice: t(
      'Answer_must_be_one_of_the_provided_choices',
      'Answer must be one of the provided choices',
    ),
    BooleanExpected: t('Answer_must_be_a_boolean', 'Answer must be a boolean'),
    RequiredField: t('This_is_a_required_field', 'This is a required field'),
  };

  return errorMap;
};
